/* Minori · sessione.
 *
 * La schermata di accesso vera vive su /accesso e funziona già: questa è la
 * sua faccia da dentro. Dice chi è entrato, fino a quando, e ripete la cosa
 * che l'utente deve poter verificare in qualunque momento — che le chiavi
 * degli exchange non passano dal browser.
 *
 * L'uscita sta in fondo: non distrugge niente, ma interrompe, e il backend non
 * ha un token di rinnovo, quindi rientrare significa riscrivere la password.
 */

import { Avvertenza, Pulsante, Scheda } from '../../componenti';
import { usaAccesso } from '../../auth/ContestoAccesso';
import { dataOra } from '../../lib/formato';
import { GruppoVoci, Voce } from './Voce';

/** Quanto manca, in italiano. `daQuanto` dice da quanto tempo è passato un
 *  momento; qui serve il verso opposto e vale la pena scriverlo invece di
 *  specchiare le date, che è il tipo di trucco che si rilegge male. */
function fraQuanto(quando: Date): string {
  const minuti = Math.round((quando.getTime() - Date.now()) / 60000);
  if (minuti <= 0) return 'è già scaduta';
  if (minuti < 60) return `fra ${minuti} ${minuti === 1 ? 'minuto' : 'minuti'}`;
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return `fra ${ore}h ${minuti % 60}m`;
  const giorni = Math.floor(ore / 24);
  return `fra ${giorni}g ${ore % 24}h`;
}

/** Scadenza letta da `exp` del JWT, SENZA verificarne la firma: serve solo a
 *  dire quando la schermata comincerà a ricevere 401. Non è una verifica di
 *  sicurezza e non viene usata per decidere nulla di sensibile. */
function scadenza(token: string): Date | null {
  try {
    const carico = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    return typeof carico.exp === 'number' ? new Date(carico.exp * 1000) : null;
  } catch {
    return null;
  }
}

export function Sessione() {
  const { sessione, esci } = usaAccesso();

  if (!sessione) {
    return (
      <Avvertenza livello="cautela">
        Nessuna sessione attiva. Questa schermata è raggiungibile solo da dentro l'applicazione.
      </Avvertenza>
    );
  }

  const fine = scadenza(sessione.token);

  return (
    <div className="sb-colonna">
      <GruppoVoci titolo="Chi è entrato">
        <Voce chiave="Nome utente" valore={sessione.utente.username} />
        <Voce
          chiave="Identificativo"
          valore={`#${sessione.utente.id}`}
          nota="arriva dalla risposta di accesso: GET /auth/profile non lo restituisce"
        />
        <Voce
          chiave="La sessione scade"
          valore={fine ? dataOra(fine) : 'senza scadenza dichiarata'}
          nota={
            fine
              ? `${fraQuanto(fine)} — letta da exp del token, senza verificarne la firma`
              : 'il token non porta una scadenza leggibile'
          }
        />
      </GruppoVoci>

      <Avvertenza livello="nota">
        Le chiavi degli exchange non passano dal browser: restano sul backend e non sono mai
        leggibili da qui. L'unico momento in cui una chiave attraversa questa interfaccia è quando
        la si aggiunge, e da lì in poi non torna più indietro.
      </Avvertenza>

      <Avvertenza livello="cautela">
        Non esiste un token di rinnovo, né un cambio password, né una gestione utenti: alla scadenza
        c'è solo il rientro. Se una schermata comincia a rispondere "sessione scaduta", è questo.
      </Avvertenza>

      {/* In fondo: interrompe il lavoro e obbliga a riscrivere la password. */}
      <div className="sb-m-fondo">
        <span className="sb-occhiello">Uscita</span>
        <Scheda variante="incassata" style={{ marginTop: 'var(--sb-s-5)', maxWidth: 460 }}>
          <div className="sb-nota">
            Uscendo, il token viene cancellato da questo browser. I bot continuano a operare: sono
            processi del backend e non dipendono da questa finestra.
          </div>
          <div style={{ marginTop: 'var(--sb-s-6)' }}>
            <Pulsante variante="distruttivo" onClick={esci}>
              Esci dalla sessione
            </Pulsante>
          </div>
        </Scheda>
      </div>
    </div>
  );
}
